import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Backdrop,
    Badge,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Delete, Edit, FilterList, Menu as MenuIcon, Public, PublicOff, Refresh } from '@mui/icons-material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { useNavigate } from 'react-router-dom';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { Logger } from 'utils/Logger';
import { useModulePermissions } from 'hooks/useModulePermissions';
import {
    SWIPE_ACTION_WIDTH_PX,
    SWIPE_DIRECTION_LOCK_PX,
    clampSwipeOffsetAsymmetric,
    computeSnappedOffsetAsymmetric,
} from 'utils/iosSwipeRow';
import { ReelDialog } from './ReelDialog';
import { ReelPreviewDialog } from './dialogs/ReelPreviewDialog';
import { ReelExportDialog } from './dialogs/ReelExportDialog';
import { reelMakerApi } from './ReelMaker.api';
import {
    REEL_MAKER_PATH,
    Reel,
    ReelSortField,
    ReelSortOrder,
    ReelStatusFilter,
    ReelUpsertPayload,
    ReelViewMode,
    reelEditorPath,
    reelVersionLabel,
} from './ReelMaker.types';

const LOG_PREFIX = 'components/ReelMaker/ReelMaker.tsx';
const VIEWMODE_STORAGE_KEY = 'reel-maker-view-mode';

export interface ReelMakerProps {
    /** Breadcrumb / heading label (default "Reel Maker"). */
    title?: string;
}

const statusChipColor = (status: Reel['status']): 'success' | 'warning' => (status === 'published' ? 'success' : 'warning');

const ReelExportBadge: React.FC<{ reel: Reel }> = ({ reel }) => {
    const exp = reel.lastExport;
    if (!exp?.status || !exp.versionLabel) return null;
    if (exp.status === 'completed') {
        return (
            <Tooltip title={`${exp.versionLabel} video ready to download`}>
                <Badge
                    badgeContent={exp.versionLabel}
                    color="success"
                    overlap="circular"
                    sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '0.58rem',
                            fontWeight: 800,
                            fontFamily: 'monospace',
                            height: 16,
                            minWidth: 16,
                            px: 0.4,
                        },
                    }}
                >
                    <Box
                        aria-label={`${exp.versionLabel} video ready`}
                        sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'success.main',
                            color: '#fff',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        }}
                    >
                        <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                </Badge>
            </Tooltip>
        );
    }
    const label =
        exp.status === 'queued' ? `${exp.versionLabel} export queued`
        : exp.status === 'in-progress'
            ? `${exp.versionLabel} exporting${typeof exp.percent === 'number' ? ` ${exp.percent}%` : '…'}`
        : exp.status === 'failed' ? `${exp.versionLabel} export failed`
        : '';
    if (!label) return null;
    const color = exp.status === 'failed' ? 'error' : 'info';
    const tip = exp.status === 'in-progress'
        ? [
            exp.stage ? exp.stage : '',
            typeof exp.percent === 'number' ? `${exp.percent}%` : '',
            exp.worker ? `on ${exp.worker}` : '',
        ].filter(Boolean).join(' · ')
        : exp.status === 'failed' && exp.error
            ? exp.error
            : '';
    const chip = (
        <Chip
            label={label}
            color={color}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, backgroundColor: 'background.paper' }}
        />
    );
    return tip ? <Tooltip title={tip}><span>{chip}</span></Tooltip> : chip;
};

const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

/** Longest tile edge in px. The other edge follows the reel's own aspect ratio. */
const TILE_LONG_EDGE = 300;

const reelTileSize = (width: number, height: number): { width: number; height: number } => {
    const reelWidth = Math.max(1, width);
    const reelHeight = Math.max(1, height);
    if (reelHeight >= reelWidth) {
        return { width: Math.max(120, Math.round(TILE_LONG_EDGE * (reelWidth / reelHeight))), height: TILE_LONG_EDGE };
    }
    return { width: TILE_LONG_EDGE, height: Math.max(120, Math.round(TILE_LONG_EDGE * (reelHeight / reelWidth))) };
};

/** Tile face: exported video behind the size label, with a scrim so the type stays readable. */
const ReelTileFace: React.FC<{ reel: Reel; playing: boolean }> = ({ reel, playing }) => {
    const theme = useTheme();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const previewUrl = reel.lastExport?.status === 'completed' ? reel.lastExport.previewUrl : null;
    const exporting = reel.lastExport?.status === 'in-progress';
    const isDark = theme.palette.mode === 'dark';
    const heroFg = theme.palette.dashboard?.heroTileForegroundColor;
    const heroLabel = theme.palette.dashboard?.heroTileLabelColor ?? (heroFg ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255,255,255,0.7)');
    const heroShadow = heroFg ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
    const titleColor = previewUrl ? (isDark ? '#fff' : 'rgba(0,0,0,0.9)') : (heroFg ?? '#fff');
    const labelColor = previewUrl ? (isDark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.72)') : heroLabel;
    const textShadow = previewUrl ? (isDark ? '0 1px 4px rgba(0,0,0,0.65)' : '0 1px 2px rgba(255,255,255,0.85)') : heroShadow;

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !previewUrl) return;
        if (playing) {
            video.muted = true;
            void video.play().catch(() => undefined);
            return;
        }
        video.pause();
        try {
            video.currentTime = 0.1;
        } catch {
            /* metadata may not be ready yet */
        }
    }, [playing, previewUrl]);

    return (
        <>
            {previewUrl && (
                <Box
                    component="video"
                    ref={videoRef}
                    src={`${previewUrl}#t=0.1`}
                    muted
                    playsInline
                    loop
                    preload="metadata"
                    aria-hidden
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                />
            )}
            {previewUrl && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: isDark ? 'rgba(0,0,0,0.48)' : 'rgba(255,255,255,0.58)',
                    }}
                />
            )}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    px: 1,
                    height: '100%',
                }}
            >
                {exporting ? (
                    <Typography sx={{ fontSize: '2.4rem', fontWeight: 800, color: titleColor, lineHeight: 1, textShadow }}>
                        {typeof reel.lastExport?.percent === 'number' ? `${reel.lastExport.percent}%` : '…'}
                    </Typography>
                ) : (
                    !previewUrl && <SlideshowOutlinedIcon sx={{ fontSize: 40, color: titleColor, opacity: 0.85 }} />
                )}
                <Typography sx={{ fontSize: exporting ? '0.95rem' : '1.5rem', fontWeight: 800, color: titleColor, lineHeight: 1, textShadow }}>
                    {reel.width} × {reel.height}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: labelColor, textTransform: 'uppercase', letterSpacing: 1, textShadow }}>
                    {exporting ? 'Exporting' : 'Canvas px'}
                </Typography>
            </Box>
        </>
    );
};

/**
 * Beam on Screen → Reel Maker list (`/#/beamonscreen/reel-maker`).
 *
 * Breadcrumb, Data Sets–style toolbar (search / sort / order / status / view toggle / NEW),
 * reels as tiles or a table, with Edit → canvas editor, Publish/Unpublish and Delete
 * (disabled while published).
 */
export const ReelMaker: React.FC<ReelMakerProps> = ({ title = 'Reel Maker' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { hasBeamonScreenAccess, isUserSuperAdmin } = useModulePermissions();
    const canManage = hasBeamonScreenAccess() || isUserSuperAdmin;

    const heroGradient =
        theme.palette.dashboard?.counterHeroGradient ??
        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;

    const [reels, setReels] = useState<Reel[]>([]);
    const [loading, setLoading] = useState(false);
    const [busyReelId, setBusyReelId] = useState<string | null>(null);

    /** Mobile/tablet: iOS-style swipe on reel tiles (same interaction as the Data Sets tiles). */
    const [tileSwipeRow, setTileSwipeRow] = useState<{ tileId: string; offset: number; dragging: boolean } | null>(null);
    const swipeSessionRef = useRef<{
        tileId: string;
        startX: number;
        startY: number;
        baseOffset: number;
        lock: 'h' | 'v' | null;
    } | null>(null);
    const latestSwipeOffsetRef = useRef(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<ReelSortField>('updatedAt');
    const [sortOrder, setSortOrder] = useState<ReelSortOrder>('desc');
    const [statusFilter, setStatusFilter] = useState<ReelStatusFilter>('all');
    const [viewMode, setViewMode] = useState<ReelViewMode>(() => {
        try {
            return localStorage.getItem(VIEWMODE_STORAGE_KEY) === 'table' ? 'table' : 'tile';
        } catch {
            return 'tile';
        }
    });

    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogReel, setDialogReel] = useState<Reel | null>(null);
    const [dialogSaving, setDialogSaving] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);

    const [deleteTarget, setDeleteTarget] = useState<Reel | null>(null);
    const [previewTarget, setPreviewTarget] = useState<Reel | null>(null);
    const [exportTarget, setExportTarget] = useState<Reel | null>(null);
    const [hoveredReelId, setHoveredReelId] = useState<string | null>(null);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const showMessage = (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
        setSnackbar({ open: true, message, severity });

    const changeViewMode = (next: ReelViewMode | null) => {
        if (!next) return;
        setViewMode(next);
        try {
            localStorage.setItem(VIEWMODE_STORAGE_KEY, next);
        } catch {
            /* ignore */
        }
    };

    const fetchReels = useCallback(async (opts?: { silent?: boolean }) => {
        if (!opts?.silent) setLoading(true);
        try {
            const list = await reelMakerApi.listReels({ status: statusFilter, sortBy: sortField, sortOrder });
            setReels(list);
        } catch (error) {
            Logger.error(LOG_PREFIX, `fetchReels failed: ${error}`);
            if (!opts?.silent) {
                showMessage(`Failed to load reels: ${error instanceof Error ? error.message : String(error)}`, 'error');
            }
        } finally {
            if (!opts?.silent) setLoading(false);
        }
    }, [statusFilter, sortField, sortOrder]);

    useEffect(() => {
        void fetchReels();
    }, [fetchReels]);

    const exportInFlight = reels.some((reel) => reel.lastExport?.status === 'queued' || reel.lastExport?.status === 'in-progress');
    useEffect(() => {
        if (!exportInFlight) return;
        const id = window.setInterval(() => { void fetchReels({ silent: true }); }, 10_000);
        return () => window.clearInterval(id);
    }, [exportInFlight, fetchReels]);

    /** Search is applied client-side on the loaded list (server already filtered by status + sorted). */
    const visibleReels = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return reels;
        return reels.filter((r) => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));
    }, [reels, searchTerm]);

    const openNewDialog = () => {
        setDialogReel(null);
        setDialogError(null);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (dialogSaving) return;
        setDialogOpen(false);
        setDialogReel(null);
        setDialogError(null);
    };

    const handleSaveReel = async (payload: ReelUpsertPayload) => {
        setDialogSaving(true);
        setDialogError(null);
        try {
            if (dialogReel) {
                await reelMakerApi.updateReel(dialogReel._id, payload);
                showMessage('Reel updated');
            } else {
                await reelMakerApi.createReel(payload);
                showMessage('Reel created');
            }
            setDialogOpen(false);
            setDialogReel(null);
            await fetchReels();
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            Logger.error(LOG_PREFIX, `handleSaveReel failed: ${message}`);
            setDialogError(message);
        } finally {
            setDialogSaving(false);
        }
    };

    const handleToggleStatus = async (reel: Reel) => {
        const next = reel.status === 'published' ? 'unpublished' : 'published';
        setTileSwipeRow(null);
        setBusyReelId(reel._id);
        try {
            const updated = await reelMakerApi.setReelStatus(reel._id, next);
            setReels((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
            showMessage(next === 'published' ? `"${reel.name}" published` : `"${reel.name}" unpublished`);
            if (statusFilter !== 'all') void fetchReels();
        } catch (error) {
            showMessage(`Failed to update status: ${error instanceof Error ? error.message : String(error)}`, 'error');
        } finally {
            setBusyReelId(null);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        setDeleteTarget(null);
        setBusyReelId(target._id);
        try {
            await reelMakerApi.deleteReel(target._id);
            setReels((prev) => prev.filter((r) => r._id !== target._id));
            showMessage(`"${target.name}" deleted`);
        } catch (error) {
            showMessage(`Failed to delete reel: ${error instanceof Error ? error.message : String(error)}`, 'error');
        } finally {
            setBusyReelId(null);
        }
    };

    const openEditor = (reel: Reel) => navigate(reelEditorPath(reel._id));

    /** Export is only possible once at least one version has been published. */
    const canExport = (reel: Reel) => canManage && (reel.publishedVersion ?? 0) > 0;
    const exportTooltip = (reel: Reel) => (canExport(reel) ? 'Export as video (MP4)' : 'Publish the reel before exporting');

    /** Left strip (swipe right): Preview + Edit [+ Export + Publish/Unpublish]; right strip (swipe left): Delete when allowed. */
    const swipeStripWidths = (reel: Reel) => ({
        left: SWIPE_ACTION_WIDTH_PX * (canManage ? 4 : 2),
        right: canManage && reel.status !== 'published' ? SWIPE_ACTION_WIDTH_PX : 0,
    });

    const handleTileTouchStart = useCallback(
        (e: React.TouchEvent, reel: Reel) => {
            if (!isMobile) return;
            const t = e.touches[0];
            setTileSwipeRow((prev) => {
                const anotherWasOpen = Boolean(prev && prev.tileId !== reel._id && Math.abs(prev.offset) > 0.5);
                const baseOffset = !anotherWasOpen && prev?.tileId === reel._id ? prev!.offset : 0;
                swipeSessionRef.current = { tileId: reel._id, startX: t.clientX, startY: t.clientY, baseOffset, lock: null };
                latestSwipeOffsetRef.current = baseOffset;
                return { tileId: reel._id, offset: baseOffset, dragging: true };
            });
        },
        [isMobile],
    );

    const handleTileTouchMove = useCallback(
        (e: React.TouchEvent, reel: Reel) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== reel._id) return;
            const touch = e.touches[0];
            const dx = touch.clientX - sess.startX;
            const dy = touch.clientY - sess.startY;

            if (sess.lock === null) {
                if (Math.abs(dx) > SWIPE_DIRECTION_LOCK_PX && Math.abs(dx) > Math.abs(dy) * 1.2) {
                    sess.lock = 'h';
                } else if (Math.abs(dy) > SWIPE_DIRECTION_LOCK_PX && Math.abs(dy) > Math.abs(dx) * 1.2) {
                    sess.lock = 'v';
                }
            }
            if (sess.lock !== 'h') return;

            const { left, right } = swipeStripWidths(reel);
            const clamped = clampSwipeOffsetAsymmetric(sess.baseOffset + dx, left, right);
            latestSwipeOffsetRef.current = clamped;
            setTileSwipeRow({ tileId: reel._id, offset: clamped, dragging: true });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMobile, canManage],
    );

    const handleTileTouchEnd = useCallback(
        (e: React.TouchEvent, reel: Reel) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== reel._id) {
                swipeSessionRef.current = null;
                return;
            }
            const t = e.changedTouches[0];
            const dx = t.clientX - sess.startX;
            const dy = t.clientY - sess.startY;
            const { lock, baseOffset } = sess;
            swipeSessionRef.current = null;
            const { left, right } = swipeStripWidths(reel);

            const finishHorizontalSnap = (off: number) => {
                const snapped = computeSnappedOffsetAsymmetric(off, left, right);
                latestSwipeOffsetRef.current = snapped;
                setTileSwipeRow({ tileId: reel._id, offset: snapped, dragging: false });
            };

            if (lock === null && Math.abs(dx) > Math.abs(dy) * 1.2 && Math.abs(dx) > 18) {
                finishHorizontalSnap(clampSwipeOffsetAsymmetric(baseOffset + dx, left, right));
                return;
            }

            if (lock !== 'h') {
                if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
                    if (Math.abs(baseOffset) > 0.5) {
                        // Tap on an open row closes it
                        latestSwipeOffsetRef.current = 0;
                        setTileSwipeRow({ tileId: reel._id, offset: 0, dragging: false });
                    } else {
                        openEditor(reel);
                    }
                } else {
                    latestSwipeOffsetRef.current = baseOffset;
                    setTileSwipeRow({ tileId: reel._id, offset: baseOffset, dragging: false });
                }
                return;
            }

            finishHorizontalSnap(latestSwipeOffsetRef.current);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMobile, canManage, navigate],
    );

    const handleTileTouchCancel = useCallback(
        (_e: React.TouchEvent, reel: Reel) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== reel._id) return;
            swipeSessionRef.current = null;
            const { left, right } = swipeStripWidths(reel);
            const snapped = computeSnappedOffsetAsymmetric(latestSwipeOffsetRef.current, left, right);
            latestSwipeOffsetRef.current = snapped;
            setTileSwipeRow({ tileId: reel._id, offset: snapped, dragging: false });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMobile, canManage],
    );

    /** Same small contained icon buttons the Data Sets table uses for its row actions. */
    const actionButtonSx = { borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 } as const;

    const renderRowActions = (reel: Reel) => {
        const isPublished = reel.status === 'published';
        const busy = busyReelId === reel._id;
        return (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                <Tooltip title="Preview">
                    <Button
                        color="info"
                        variant="contained"
                        size="small"
                        aria-label={`Preview ${reel.name}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setPreviewTarget(reel);
                        }}
                        sx={actionButtonSx}
                    >
                        <PlayCircleOutlineIcon fontSize="small" />
                    </Button>
                </Tooltip>
                {canManage && (
                    <Tooltip title={exportTooltip(reel)}>
                        <span>
                            <Button
                                color="info"
                                variant="contained"
                                size="small"
                                aria-label={`Export ${reel.name}`}
                                disabled={!canExport(reel)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExportTarget(reel);
                                }}
                                sx={actionButtonSx}
                            >
                                <FileDownloadOutlinedIcon fontSize="small" />
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title="Edit (open canvas)">
                    <Button
                        color="secondary"
                        variant="contained"
                        size="small"
                        aria-label={`Edit ${reel.name}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            openEditor(reel);
                        }}
                        sx={actionButtonSx}
                    >
                        <Edit fontSize="small" />
                    </Button>
                </Tooltip>
                {canManage && (
                    <Tooltip title={isPublished ? 'Unpublish' : 'Publish'}>
                        <span>
                            <Button
                                color={isPublished ? 'warning' : 'success'}
                                variant="contained"
                                size="small"
                                aria-label={`${isPublished ? 'Unpublish' : 'Publish'} ${reel.name}`}
                                disabled={busy}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void handleToggleStatus(reel);
                                }}
                                sx={actionButtonSx}
                            >
                                {busy ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : isPublished ? (
                                    <PublicOff fontSize="small" />
                                ) : (
                                    <Public fontSize="small" />
                                )}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {canManage && (
                    <Tooltip title={isPublished ? 'Unpublish the reel before deleting it' : 'Delete'}>
                        <span>
                            <Button
                                color="error"
                                variant="contained"
                                size="small"
                                aria-label={`Delete ${reel.name}`}
                                disabled={isPublished || busy}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget(reel);
                                }}
                                sx={actionButtonSx}
                            >
                                <Delete fontSize="small" />
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Box>
        );
    };

    const filterControls = (fullWidth: boolean) => (
        <>
            <FormControl sx={fullWidth ? undefined : { minWidth: 150 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Sort By</InputLabel>
                <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value as ReelSortField)}>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="updatedAt">Last Updated On</MenuItem>
                    <MenuItem value="createdAt">Created On</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                </Select>
            </FormControl>
            <FormControl sx={fullWidth ? undefined : { minWidth: 100 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Order</InputLabel>
                <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as ReelSortOrder)}>
                    <MenuItem value="asc">ASC</MenuItem>
                    <MenuItem value="desc">DSC</MenuItem>
                </Select>
            </FormControl>
            <FormControl sx={fullWidth ? undefined : { minWidth: 140 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value as ReelStatusFilter)}>
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="unpublished">Unpublished</MenuItem>
                </Select>
            </FormControl>
        </>
    );

    const viewToggle = (
        <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, next) => changeViewMode(next)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
        >
            <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View">
                    <ViewModuleIcon fontSize="small" />
                </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View">
                    <TableRowsIcon fontSize="small" />
                </Tooltip>
            </ToggleButton>
        </ToggleButtonGroup>
    );

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            {loading && (
                <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}

            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: REEL_MAKER_PATH, itemLabel: title },
                ]}
            />
            {/* Mobile / tablet: prominent NEW above toolbar */}
            {canManage && (
                <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 2, px: { xs: 0.5, sm: 1 } }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Add />}
                        onClick={openNewDialog}
                        fullWidth
                        sx={{
                            maxWidth: 480,
                            py: 1.25,
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            borderWidth: 2,
                            textTransform: 'uppercase',
                            '&:hover': { borderWidth: 2 },
                        }}
                    >
                        New Reel
                    </Button>
                </Box>
            )}

            {/* Toolbar */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                    mb: 3,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton
                        color="secondary"
                        onClick={() => setMobileFiltersOpen(true)}
                        size="large"
                        aria-label="Search and filters"
                        sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                    >
                        <FilterList />
                    </IconButton>
                </Box>
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
                    <IconButton
                        color="secondary"
                        onClick={() => setMobileActionsOpen(true)}
                        size="large"
                        aria-label="Toolbar actions"
                        sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <TextField
                        label="Search Reel"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flexGrow: 1, minWidth: 200 }}
                    />
                </Box>

                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                    {filterControls(false)}
                </Box>

                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    {viewToggle}
                    <Tooltip title="Refresh">
                        <span>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                aria-label="Refresh reels"
                                onClick={() => { void fetchReels(); }}
                                disabled={loading}
                                sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
                            >
                                <Refresh fontSize="small" />
                            </Button>
                        </span>
                    </Tooltip>
                    {canManage && (
                        <Button variant="contained" color="secondary" startIcon={<Add />} onClick={openNewDialog} size="small" sx={{ whiteSpace: 'nowrap' }}>
                            New
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Mobile: search & filters */}
            <Dialog open={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Search & Filters</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Search Reel" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} fullWidth />
                        {filterControls(true)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileFiltersOpen(false)} variant="outlined" color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mobile: toolbar actions */}
            <Dialog open={mobileActionsOpen} onClose={() => setMobileActionsOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Actions</DialogTitle>
                <DialogContent>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                            View:
                        </Typography>
                        {viewToggle}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileActionsOpen(false)} variant="outlined" color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Empty state */}
            {!loading && visibleReels.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <SlideshowOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {reels.length === 0 ? 'No reels yet' : 'No reels match your search'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {reels.length === 0 && canManage ? 'Click NEW to create your first reel.' : 'Try a different search or status filter.'}
                    </Typography>
                </Paper>
            )}

            {/* Tile view — same layout as the Data Sets tiles: gradient hero, chips top-left,
                bottom name overlay that expands on hover to reveal details + action buttons */}
            {viewMode === 'tile' && visibleReels.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                    {visibleReels.map((reel) => {
                        const isPublished = reel.status === 'published';
                        const busy = busyReelId === reel._id;
                        const orientationLabel = reel.height > reel.width ? 'Portrait' : reel.height === reel.width ? 'Square' : 'Landscape';
                        const tileSize = reelTileSize(reel.width, reel.height);

                        if (isMobile) {
                            const canDeleteReel = canManage && !isPublished;
                            const rowOffset = tileSwipeRow?.tileId === reel._id ? tileSwipeRow.offset : 0;
                            const rowDragging = tileSwipeRow?.tileId === reel._id && tileSwipeRow.dragging;

                            /* Mobile / tablet: iOS-style swipe — right reveals Edit + Publish, left reveals Delete */
                            return (
                                <Box
                                    key={reel._id}
                                    sx={{
                                        width: tileSize.width,
                                        height: tileSize.height,
                                        borderRadius: 2.5,
                                        overflow: 'hidden',
                                        position: 'relative',
                                        flexShrink: 0,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        touchAction: 'pan-y',
                                        isolation: 'isolate',
                                    }}
                                >
                                    {/* Action strips behind the card */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'stretch',
                                            zIndex: 0,
                                            borderRadius: 2.5,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: SWIPE_ACTION_WIDTH_PX,
                                                flexShrink: 0,
                                                bgcolor: 'info.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Tooltip title="Preview">
                                                <IconButton
                                                    aria-label={`Preview ${reel.name}`}
                                                    size="medium"
                                                    sx={{ color: 'info.contrastText', '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' } }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTileSwipeRow(null);
                                                        setPreviewTarget(reel);
                                                    }}
                                                >
                                                    <PlayCircleOutlineIcon fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {canManage && (
                                            <Box
                                                sx={{
                                                    width: SWIPE_ACTION_WIDTH_PX,
                                                    flexShrink: 0,
                                                    bgcolor: 'info.dark',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Tooltip title={exportTooltip(reel)}>
                                                    <span>
                                                        <IconButton
                                                            aria-label={`Export ${reel.name}`}
                                                            size="medium"
                                                            disabled={!canExport(reel)}
                                                            sx={{ color: 'info.contrastText', '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' } }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setTileSwipeRow(null);
                                                                setExportTarget(reel);
                                                            }}
                                                        >
                                                            <FileDownloadOutlinedIcon fontSize="medium" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        )}
                                        <Box
                                            sx={{
                                                width: SWIPE_ACTION_WIDTH_PX,
                                                flexShrink: 0,
                                                bgcolor: 'secondary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    aria-label={`Edit ${reel.name}`}
                                                    size="medium"
                                                    sx={{ color: 'secondary.contrastText', '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' } }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTileSwipeRow(null);
                                                        openEditor(reel);
                                                    }}
                                                >
                                                    <Edit fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {canManage && (
                                            <Box
                                                sx={{
                                                    width: SWIPE_ACTION_WIDTH_PX,
                                                    flexShrink: 0,
                                                    bgcolor: isPublished ? 'warning.main' : 'success.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Tooltip title={isPublished ? 'Unpublish' : 'Publish'}>
                                                    <span>
                                                        <IconButton
                                                            aria-label={`${isPublished ? 'Unpublish' : 'Publish'} ${reel.name}`}
                                                            size="medium"
                                                            disabled={busy}
                                                            sx={{
                                                                color: isPublished ? 'warning.contrastText' : 'success.contrastText',
                                                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                void handleToggleStatus(reel);
                                                            }}
                                                        >
                                                            {busy ? (
                                                                <CircularProgress size={20} color="inherit" />
                                                            ) : isPublished ? (
                                                                <PublicOff fontSize="medium" />
                                                            ) : (
                                                                <Public fontSize="medium" />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        )}
                                        <Box sx={{ flex: 1, minWidth: 0 }} />
                                        {canDeleteReel && (
                                            <Box
                                                sx={{
                                                    width: SWIPE_ACTION_WIDTH_PX,
                                                    flexShrink: 0,
                                                    bgcolor: 'error.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        aria-label={`Delete ${reel.name}`}
                                                        size="medium"
                                                        sx={{ color: 'error.contrastText', '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' } }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setTileSwipeRow(null);
                                                            setDeleteTarget(reel);
                                                        }}
                                                    >
                                                        <Delete fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Sliding card */}
                                    <Box
                                        onTouchStart={(e) => handleTileTouchStart(e, reel)}
                                        onTouchMove={(e) => handleTileTouchMove(e, reel)}
                                        onTouchEnd={(e) => handleTileTouchEnd(e, reel)}
                                        onTouchCancel={(e) => handleTileTouchCancel(e, reel)}
                                        sx={{
                                            position: 'relative',
                                            zIndex: 1,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 2.5,
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            backgroundColor: 'background.paper',
                                            transform: `translateX(${rowOffset}px)`,
                                            transition: rowDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                            willChange: rowDragging ? 'transform' : 'auto',
                                            boxShadow: rowOffset !== 0 ? 3 : 'none',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minHeight: 0,
                                                background: heroGradient,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                position: 'relative',
                                            }}
                                        >
                                            <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5 }}>
                                                <Chip
                                                    label={reel.status}
                                                    color={statusChipColor(reel.status)}
                                                    size="small"
                                                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', textTransform: 'capitalize' }}
                                                />
                                                <Chip
                                                    label={orientationLabel}
                                                    color="secondary"
                                                    size="small"
                                                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                                                />
                                                <Chip
                                                    label={reelVersionLabel(reel)}
                                                    size="small"
                                                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                                                />
                                                <ReelExportBadge reel={reel} />
                                            </Box>
                                            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                                                <ReelTileFace reel={reel} playing={false} />
                                            </Box>
                                            <Box
                                                sx={{
                                                    background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 55%, rgba(0,0,0,0.2) 100%)',
                                                    px: 1.5,
                                                    py: 1.25,
                                                    minHeight: 48,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                        fontSize: '0.88rem',
                                                        lineHeight: 1.35,
                                                        textShadow: '0 1px 4px rgba(0,0,0,0.45)',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {reel.name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }

                        return (
                            <Box
                                key={reel._id}
                                onClick={() => openEditor(reel)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openEditor(reel);
                                    }
                                }}
                                onMouseEnter={() => setHoveredReelId(reel._id)}
                                onMouseLeave={() => setHoveredReelId((current) => (current === reel._id ? null : current))}
                                sx={{
                                    width: tileSize.width,
                                    height: tileSize.height,
                                    borderRadius: 2.5,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover .tile-overlay': { height: 120 },
                                    '&:hover .tile-details': { opacity: 1 },
                                    '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                                    '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 2 },
                                }}
                            >
                                {/* Hero */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 0,
                                        background: heroGradient,
                                    }}
                                >
                                    <ReelTileFace reel={reel} playing={hoveredReelId === reel._id} />
                                </Box>

                                {/* Chips */}
                                <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5 }}>
                                    <Chip
                                        label={reel.status}
                                        color={statusChipColor(reel.status)}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            color: '#fff',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                            textTransform: 'capitalize',
                                        }}
                                    />
                                    <Chip
                                        label={orientationLabel}
                                        color="secondary"
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                        }}
                                    />
                                    <Chip
                                        label={reelVersionLabel(reel)}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            fontFamily: 'monospace',
                                            color: '#fff',
                                            backgroundColor: 'rgba(0,0,0,0.55)',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                        }}
                                    />
                                    <ReelExportBadge reel={reel} />
                                </Box>

                                {/* Bottom overlay */}
                                    <Box
                                        className="tile-overlay"
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            zIndex: 3,
                                            overflow: 'hidden',
                                        background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                                        transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                        height: 40,
                                        px: 1.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        noWrap
                                        className="tile-name-short"
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: '0.82rem',
                                            lineHeight: '40px',
                                            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                                            flexShrink: 0,
                                            transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                                        }}
                                    >
                                        {reel.name}
                                    </Typography>

                                    <Box
                                        className="tile-details"
                                        sx={{
                                            opacity: 0,
                                            transition: 'opacity 0.3s ease 0.08s',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            pt: 0.25,
                                            pb: 1,
                                            flex: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            noWrap
                                            title={reel.name}
                                            sx={{
                                                color: '#fff',
                                                fontWeight: 700,
                                                fontSize: '0.78rem',
                                                lineHeight: 1.3,
                                                textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                                            }}
                                        >
                                            {reel.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            noWrap
                                            title={reel.description}
                                            sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}
                                        >
                                            {reel.description || 'No description'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                                            Updated: {new Date(reel.updatedAt).toLocaleDateString()}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                            <Box display="flex" gap={0.5}>
                                                <Tooltip title="Preview">
                                                    <Button
                                                        color="info"
                                                        variant="contained"
                                                        size="small"
                                                        aria-label={`Preview ${reel.name}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPreviewTarget(reel);
                                                        }}
                                                        sx={actionButtonSx}
                                                    >
                                                        <PlayCircleOutlineIcon fontSize="small" />
                                                    </Button>
                                                </Tooltip>
                                                {canManage && (
                                                    <Tooltip title={exportTooltip(reel)}>
                                                        <span>
                                                            <Button
                                                                color="info"
                                                                variant="contained"
                                                                size="small"
                                                                aria-label={`Export ${reel.name}`}
                                                                disabled={!canExport(reel)}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setExportTarget(reel);
                                                                }}
                                                                sx={actionButtonSx}
                                                            >
                                                                <FileDownloadOutlinedIcon fontSize="small" />
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Edit (open canvas)">
                                                    <Button
                                                        color="secondary"
                                                        variant="contained"
                                                        size="small"
                                                        aria-label={`Edit ${reel.name}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openEditor(reel);
                                                        }}
                                                        sx={actionButtonSx}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </Button>
                                                </Tooltip>
                                                {canManage && (
                                                    <Tooltip title={isPublished ? 'Unpublish' : 'Publish'}>
                                                        <span>
                                                            <Button
                                                                color={isPublished ? 'warning' : 'success'}
                                                                variant="contained"
                                                                size="small"
                                                                aria-label={`${isPublished ? 'Unpublish' : 'Publish'} ${reel.name}`}
                                                                disabled={busy}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    void handleToggleStatus(reel);
                                                                }}
                                                                sx={actionButtonSx}
                                                            >
                                                                {busy ? (
                                                                    <CircularProgress size={16} color="inherit" />
                                                                ) : isPublished ? (
                                                                    <PublicOff fontSize="small" />
                                                                ) : (
                                                                    <Public fontSize="small" />
                                                                )}
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Box display="flex" gap={0.5}>
                                                {canManage && (
                                                    <Tooltip title={isPublished ? 'Unpublish the reel before deleting it' : 'Delete'}>
                                                        <span>
                                                            <Button
                                                                color="error"
                                                                variant="contained"
                                                                size="small"
                                                                aria-label={`Delete ${reel.name}`}
                                                                disabled={isPublished || busy}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDeleteTarget(reel);
                                                                }}
                                                                sx={actionButtonSx}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Table view */}
            {viewMode === 'table' && visibleReels.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                                <TableCell align="right">Size (px)</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Version</TableCell>
                                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Created</TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Updated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleReels.map((reel) => (
                                <TableRow key={reel._id} hover sx={{ cursor: 'pointer' }} onClick={() => openEditor(reel)}>
                                    <TableCell sx={{ fontWeight: 600 }}>{reel.name}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 360 }}>
                                        <Typography variant="body2" noWrap title={reel.description}>
                                            {reel.description || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {reel.width} × {reel.height}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={reel.status} color={statusChipColor(reel.status)} variant="outlined" size="small" sx={{ textTransform: 'capitalize' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={reelVersionLabel(reel)} variant="outlined" size="small" sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                                        <Box component="span" sx={{ ml: 0.75 }}><ReelExportBadge reel={reel} /></Box>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{formatDate(reel.createdAt)}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(reel.updatedAt)}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{renderRowActions(reel)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ReelDialog
                open={dialogOpen}
                reel={dialogReel}
                saving={dialogSaving}
                error={dialogError}
                onClose={closeDialog}
                onSave={handleSaveReel}
            />

            <ReelPreviewDialog open={Boolean(previewTarget)} reel={previewTarget} onClose={() => setPreviewTarget(null)} />
            <ReelExportDialog
                open={Boolean(exportTarget)}
                reel={exportTarget}
                onClose={() => setExportTarget(null)}
                onQueued={(result) => {
                    showMessage(result.message, 'info');
                    void fetchReels({ silent: true });
                }}
            />

            {/* Delete confirmation */}
            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete reel?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        “{deleteTarget?.name}” will be permanently removed. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} variant="outlined" color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={() => void handleConfirmDelete()} variant="contained" color="error" startIcon={<Delete />}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ReelMaker;
